import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  motion, useScroll, useTransform, useMotionValueEvent, useMotionTemplate,
  useReducedMotion, useInView, MotionValue,
} from 'framer-motion';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import JournalCanvas from './JournalCanvas';
import SimpleColorPicker, { TextColors } from './TempColorPicker';
import { libraryDb } from '../library/libraryDb';
import { Draft, ImagePlacement, newEntryId, blobToDataUrl, dataUrlToBlob, draftKey } from '../library/entry';
import { snapshotPreview, encodePreview, isMostlyBlank } from '../library/preview';
import { newSeed, pick } from '../chaos/rng';
import { useChaos } from '../chaos/ChaosContext';
import { useTypingRhythm } from '../storm/useTypingRhythm';
import { useStorm } from '../storm/StormContext';
import GlitchText from '../chaos/GlitchText';
import { useLeanFrame } from '../play/useLeanFrame';
import { useMagneticButton } from '../play/useMagneticButton';
import { useBookGrab } from '../play/useBookGrab';
import { useTossTile } from '../play/useTossTile';
import { fx } from '../play/EffectsCanvas';
import FollowUp from './write/FollowUp';
import SaveSequence from './write/SaveSequence';
import Lenis from 'lenis';
import 'react-datepicker/dist/react-datepicker.css'; // base layout the .guided-date skin overrides
import './GuidedJournal.css';

// ── Guided journaling as a SCROLL experience (Cuberto / Active-Theory energy).
// You descend through full-screen sections. Each question's type rises out of a mask,
// giant index numerals parallax behind it, and the storm escalates with scroll depth.
// At the bottom the journal assembles itself as you scroll into it. Scroll back up to edit.

const UI = 'var(--font-ui)';
const EXPO = [0.16, 1, 0.3, 1] as const;
const COARSE = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

const LAST_LOC_KEY = 'mania_last_location';
const SECTIONS = 6;
const AUTOSAVE_MS = 500;
const DEFAULT_LOC = 'MANIA, LA JOLLA, CA';

async function downscale(dataUrl: string, max = 1280, quality = 0.72): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      if (!ctx) { resolve(dataUrl); return; }
      ctx.drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

// Editorial palette — cream ink + faint cream hairlines on transparent, matching the
// re-skinned calendar (no boxy filled fields, no bright borders).
const C = {
  ink: '#f2f0e9', muted: 'rgba(242,240,233,0.55)', line: 'rgba(242,240,233,0.12)',
  line2: 'rgba(242,240,233,0.18)', field: 'transparent',
};

// A scroll section. Its motion is derived from the CONTAINER scroll progress (reliable),
// not a per-element useScroll: section `index` is centered at progress index/(total-1),
// so we build a window around that center. Content fades/parallaxes; a giant index
// numeral drifts behind it at a stronger rate.
const Section: React.FC<{
  progress: MotionValue<number>; index: number; total: number;
  reduce: boolean; children: React.ReactNode;
}> = ({ progress, index, total, reduce, children }) => {
  const center = index / (total - 1);
  const step = 1 / (total - 1);
  const opacity = useTransform(progress, [center - step, center - step * 0.45, center + step * 0.45, center + step], [0, 1, 1, 0]);
  // Scroll-linked 3D pass-through (srikarsunchu signature): the block tilts back as it
  // rises from below, sits flat & full-scale at center, then tilts forward as it leaves.
  // GPU-only (translateY/rotateX/scale + opacity); the curve is implicit in the linear
  // scroll mapping, so it stays interruptible — scrubbing back reverses it exactly.
  const y = useTransform(progress, [center - step, center + step], [70, -70]);
  const rotateX = useTransform(progress, [center - step, center, center + step], COARSE() ? [4, 0, -3] : [11, 0, -9]);
  const scale = useTransform(progress, [center - step, center, center + step], [0.955, 1, 0.965]);
  const sectionRef = useRef<HTMLElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  useLeanFrame(frameRef, sectionRef);

  if (reduce) {
    return (
      <section ref={sectionRef} className="gj-section">
        <div ref={frameRef} className="gj-frame storm-chrome" aria-hidden />
        <div style={{ width: '100%', maxWidth: 680, padding: '0 24px', position: 'relative', zIndex: 1 }}>{children}</div>
      </section>
    );
  }
  return (
    <section ref={sectionRef} className="gj-section" style={{ perspective: 1300 }}>
      <div ref={frameRef} className="gj-frame storm-chrome" aria-hidden />
      <motion.div style={{ opacity, y, rotateX, scale, transformPerspective: 1300, transformOrigin: '50% 50%',
        width: '100%', maxWidth: 680, padding: '0 24px', position: 'relative', zIndex: 1 }}>
        {children}
      </motion.div>
    </section>
  );
};

// Typing sparks: burst from the point on this section's .gj-frame nearest the caret's rough
// position, flying outward (away from the textarea) — never drawn inside the textarea box.
const CHAR_W = 0.5; // ≈ average glyph width / font-size for the UI face
function caretPoint(ta: HTMLTextAreaElement, r: DOMRect): { x: number; y: number } {
  const cs = window.getComputedStyle(ta);
  const fs = parseFloat(cs.fontSize) || 22;
  const lh = parseFloat(cs.lineHeight) || fs * 1.6;
  const padL = parseFloat(cs.paddingLeft) || 0, padT = parseFloat(cs.paddingTop) || 0;
  const inner = Math.max(40, ta.clientWidth - padL - (parseFloat(cs.paddingRight) || 0));
  const perRow = Math.max(1, Math.floor(inner / (fs * CHAR_W)));
  const lines = ta.value.slice(0, ta.selectionStart ?? ta.value.length).split('\n');
  let row = 0;
  for (let i = 0; i < lines.length - 1; i++) row += Math.max(1, Math.ceil(lines[i].length / perRow));
  const last = lines[lines.length - 1].length;
  row += Math.floor(last / perRow);
  const col = last % perRow;
  const x = r.left + padL + col * fs * CHAR_W;
  const y = r.top + padT + row * lh + lh / 2 - ta.scrollTop;
  return { x: Math.min(r.right, Math.max(r.left, x)), y: Math.min(r.bottom, Math.max(r.top, y)) };
}

function emitTypingSparks(ta: HTMLTextAreaElement, count: number) {
  const frame = ta.closest('section')?.querySelector('.gj-frame');
  if (!frame) return;
  const t = ta.getBoundingClientRect(), f = frame.getBoundingClientRect();
  const c = caretPoint(ta, t);
  const clampY = (y: number) => Math.min(f.bottom, Math.max(f.top, y));
  const clampX = (x: number) => Math.min(f.right, Math.max(f.left, x));
  const edges = [
    { d: c.x - f.left, x: f.left, y: clampY(c.y), dx: -1, dy: 0 },
    { d: f.right - c.x, x: f.right, y: clampY(c.y), dx: 1, dy: 0 },
    { d: c.y - f.top, x: clampX(c.x), y: f.top, dx: 0, dy: -1 },
    { d: f.bottom - c.y, x: clampX(c.x), y: f.bottom, dx: 0, dy: 1 },
  ];
  let best = edges[0];
  for (let i = 1; i < edges.length; i++) if (edges[i].d < best.d) best = edges[i];
  const inT = (x: number, y: number) => x >= t.left && x <= t.right && y >= t.top && y <= t.bottom;
  // Origin or flight path over the textarea box (e.g. it overflows the frame): skip, never draw on text.
  if (inT(best.x, best.y) || inT(best.x + best.dx * 60, best.y + best.dy * 60)) return;
  fx.sparks(best.x, best.y, count, best.dx, best.dy);
}

const NOOP = () => {};

// Photo-section thumbnail. The outer cell is the toss target (useTossTile is its only transform
// writer); the inner motion.div keeps the entrance animation and the clipping.
const PhotoTile: React.FC<{ src: string; i: number; reduce: boolean; onRemove: () => void }> = ({ src, i, reduce, onRemove }) => {
  const tileRef = useRef<HTMLDivElement>(null);
  const { revealed } = useTossTile(tileRef);
  return (
    <div ref={tileRef} className={`gj-tile${revealed ? ' is-revealed' : ''}${reduce ? ' is-calm' : ''}`}>
      <motion.div className="relative" style={{ aspectRatio: '1', borderRadius: 10, overflow: 'hidden', border: `1px solid ${C.line}` }}
        initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.92, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EXPO, delay: Math.min(i, 5) * 0.05 }}>
        <img src={src} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <button className="gj-tile-x" aria-label="Remove photo" onClick={onRemove}
          style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: 6, border: 'none', background: 'rgba(0,0,0,.6)', color: '#fff', cursor: 'pointer', fontSize: 12 }}>✕</button>
      </motion.div>
    </div>
  );
};
const EMPTY_SECTIONS = [''];

// Final section: the journal physically assembles out of the storm as you scroll in.
const RevealSection: React.FC<{
  progress: MotionValue<number>; index: number; total: number;
  scrollRef: React.RefObject<HTMLDivElement>; reduce: boolean;
  previewRef: React.RefObject<HTMLDivElement>;
  date: Date; location: string; textSections: string[]; images: string[]; textColors: TextColors;
  imageLayout: ImagePlacement[] | null; onLayout: (l: ImagePlacement[]) => void; layoutEpoch: number;
  canSave: boolean; saving: boolean; sealing: boolean; onSave: () => void;
}> = ({ progress, index, total, scrollRef, reduce, previewRef, date, location, textSections, images, textColors, imageLayout, onLayout, layoutEpoch, canSave, saving, sealing, onSave }) => {
  const { warp } = useStorm();
  const sectionRef = useRef<HTMLElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const editRef = useRef<HTMLButtonElement>(null);
  const saveRef = useRef<HTMLButtonElement>(null);
  useLeanFrame(frameRef, sectionRef);
  useMagneticButton(editRef);
  useMagneticButton(saveRef);
  // Warp once per drag gesture: re-arm after 400ms without drag callbacks.
  const lastDrag = useRef(0);
  const onDrag = useCallback(() => {
    const now = performance.now();
    if (now - lastDrag.current > 400) warp();
    lastDrag.current = now;
  }, [warp]);
  // The journal assembles over the approach to this last section (centered at progress 1).
  const center = index / (total - 1);
  const step = 1 / (total - 1);
  const scale = useTransform(progress, [center - step, center - step * 0.2], [0.5, 1]);
  const rotateX = useTransform(progress, [center - step, center - step * 0.2], [26, 0]);
  const opacity = useTransform(progress, [center - step, center - step * 0.6], [0, 1]);
  const blurN = useTransform(progress, [center - step, center - step * 0.35], [28, 0]) as MotionValue<number>;
  const filter = useMotionTemplate`blur(${blurN}px)`;
  // The book stays CLOSED until it's settled in view, THEN flips open (one-shot, ~2.2s).
  // Scroll back up out of view → it closes again. So you arrive on a closed book and watch
  // it open, instead of it animating during the scroll-in.
  const bookInView = useInView(previewRef, { root: scrollRef, amount: 0.85 });
  // Cover flicks (useBookGrab) override the in-view default until the section scrolls out and back.
  const [override, setOverride] = useState<boolean | null>(null);
  useEffect(() => { setOverride(null); }, [bookInView]);
  const open = override ?? bookInView;
  const grabRef = useRef<HTMLDivElement>(null);
  useBookGrab(grabRef, open, setOverride);
  // The page is bound into a book: a cover behind it (something to lay on) only on the
  // PERIMETER — nothing overlays the page content — and a front cover that swings open
  // to reveal the page. The book takes the title-ink color (--book).
  const journal = (
    <div ref={grabRef} className={`gj-book-grab${reduce ? ' is-calm' : ''}`}>
    <div className="gj-book" style={{ ['--book' as any]: textColors.locationColor || '#f2f0e9' }}>
      <div className="gj-book-back" aria-hidden />
      {/* Grab ring: four bands in the 18px margin around the page — never over .gj-page-wrap. */}
      <div className="gj-book-ring" aria-hidden>
        <i data-book-handle="ring" className="gj-ring-t" /><i data-book-handle="ring" className="gj-ring-b" />
        <i data-book-handle="ring" className="gj-ring-l" /><i data-book-handle="ring" className="gj-ring-r" />
      </div>
      <motion.div className="gj-page-wrap" initial={false} animate={{ opacity: sealing ? [0.4, 1] : 1 }} transition={{ duration: 0.6 }}>
        <JournalCanvas key={layoutEpoch} date={date} location={location || DEFAULT_LOC}
          textSections={textSections.length ? textSections : EMPTY_SECTIONS} images={images}
          textColors={textColors} layoutMode="freeflow" editMode onNewEntry={NOOP}
          savedImagePositions={imageLayout ?? undefined} onLayoutChange={onLayout}
          onImageDrag={onDrag} />
      </motion.div>
      {sealing && (
        // Sibling of .gj-page-wrap (never an ancestor of the canvas): typed text cross-fades out over the handwriting.
        <motion.div className="gj-morph" aria-hidden initial={{ opacity: 1 }} animate={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}>
          {textSections.join('\n\n')}
        </motion.div>
      )}
      <div className="gj-book-spine" data-book-handle="spine" aria-hidden />
      {/* Reduced motion: the cover snaps (no swing); a tap on it opens, a tap on the spine closes. */}
      <motion.div className={`gj-book-cover${open ? '' : ' is-closed'}`} data-book-handle="cover" aria-hidden
        initial={{ rotateY: 0 }} animate={{ rotateY: open ? -180 : 0 }}
        transition={reduce ? { duration: 0 } : { duration: 2.2, ease: [0.65, 0, 0.35, 1], delay: open && override === null ? 0.25 : 0 }}>
        <span className="gj-cover-mark">m</span>
      </motion.div>
    </div>
    </div>
  );

  return (
    <section ref={sectionRef} className="gj-section" style={{ perspective: 1500 }}>
      <div ref={frameRef} className="gj-frame storm-chrome" aria-hidden />
      <div className="flex flex-col items-center" style={{ width: '100%', padding: '0 24px' }}>
        {reduce ? (
          <div ref={previewRef} style={{ width: '100%', maxWidth: 'min(420px, 46vh)' }}>{journal}</div>
        ) : (
          <motion.div ref={previewRef} style={{ width: '100%', maxWidth: 'min(420px, 46vh)', scale, rotateX, opacity, filter, transformPerspective: 1500 }}>
            {journal}
          </motion.div>
        )}
        <motion.div className="flex items-center gap-4 mt-9"
          initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ root: scrollRef, amount: 0.4 }} transition={{ duration: 0.6, ease: EXPO, delay: 0.15 }}>
          {/* Plain buttons: useMagneticButton is the only writer of their transform. */}
          <button ref={editRef}
            onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })} disabled={sealing}
            style={{ background: 'transparent', color: C.ink, border: `1px solid ${C.line2}`, borderRadius: 11, padding: '12px 24px', fontSize: 15, cursor: 'pointer', fontFamily: UI }}>
            ↑ Edit
          </button>
          <button ref={saveRef} onClick={onSave} disabled={saving || sealing || !canSave}
            style={{ background: canSave ? C.ink : C.line2, color: canSave ? '#0b0b0c' : C.muted, border: 'none', borderRadius: 11, padding: '12px 28px', fontSize: 15, fontWeight: 700, cursor: saving ? 'wait' : canSave ? 'pointer' : 'not-allowed', fontFamily: UI }}>
            {saving ? 'Saving…' : 'Keep this forever'}
          </button>
        </motion.div>
        {!canSave && (
          <p style={{ color: '#e5a44d', fontSize: 13, marginTop: 12, fontFamily: UI }}>Scroll up — add your words and a location first.</p>
        )}
      </div>
    </section>
  );
};

const GuidedJournal: React.FC = () => {
  const reduce = !!useReducedMotion();
  const { setScroll, setWords, tear, warp, intensity } = useStorm();
  const { onKey, levelRef } = useTypingRhythm();
  const onWriteKey = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    onKey();
    // Sparks only for printable keys and Enter, and only the first of an auto-repeat run.
    if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.length !== 1 && e.key !== 'Enter') return;
    emitTypingSparks(e.currentTarget, 2 + Math.round(levelRef.current * 4));
  }, [onKey, levelRef]);
  const { rngFor } = useChaos();
  const introVariant = useMemo(() => pick(rngFor('intro'), ['shatter', 'scan', 'tear'] as const), [rngFor]);
  const [introSkipped, setIntroSkipped] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const contentRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ container: scrollRef });
  const barWidth = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  const cueOpacity = useTransform(scrollYProgress, [0, 0.04], [1, 0]);

  // Lenis — buttery inertia/lerp scrolling on the creation pane. It updates the real
  // scrollTop, so framer's useScroll above keeps reading it; everything stays in sync.
  const lenisRef = useRef<Lenis | null>(null);
  useEffect(() => {
    if (reduce || COARSE()) return;
    const wrapper = scrollRef.current, content = contentRef.current;
    if (!wrapper || !content) return;
    const lenis = new Lenis({ wrapper, content, lerp: 0.085, smoothWheel: true, wheelMultiplier: 1, touchMultiplier: 1.6 });
    let raf = 0;
    const loop = (t: number) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    lenisRef.current = lenis;
    return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisRef.current = null; };
  }, [reduce]);

  // The storm escalates as you descend — the "lapse", driven by scroll.
  useMotionValueEvent(scrollYProgress, 'change', (v) => { setScroll(v); });
  useEffect(() => () => setScroll(0), [setScroll]);

  // Section change → jolt the frame chrome (never the text). The index is also kept in
  // state (6 values, changes rarely) so the draft can restore the exact section.
  const sectionIdx = useRef(0);
  const [section, setSection] = useState(0);
  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const i = Math.round(Math.max(0, Math.min(1, v)) * (SECTIONS - 1));
    if (i !== sectionIdx.current) { sectionIdx.current = i; setSection(i); warp(); }
  });

  // Intro: the "tear" variant fires a storm tear on mount; any input in the first second skips the intro motion.
  useEffect(() => {
    if (reduce) return;
    if (introVariant === 'tear') tear({ strength: 0.6 });
    const skip = () => setIntroSkipped(true);
    window.addEventListener('keydown', skip); window.addEventListener('pointerdown', skip);
    const t = window.setTimeout(() => { window.removeEventListener('keydown', skip); window.removeEventListener('pointerdown', skip); }, 1000);
    return () => { window.clearTimeout(t); window.removeEventListener('keydown', skip); window.removeEventListener('pointerdown', skip); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const today = (() => { const d = new Date(); d.setHours(12, 0, 0, 0); return d; })();
  const [date, setDate] = useState<Date>(today);
  const [journalText, setJournalText] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [location, setLocation] = useState<string>(() => {
    try { return localStorage.getItem(LAST_LOC_KEY) || ''; } catch { return ''; }
  });
  const [textColors, setTextColors] = useState<TextColors>({ locationColor: '#FFFFFF', locationShadowColor: '#000000' });
  const [saving, setSaving] = useState(false);
  const [sealing, setSealing] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [imageLayout, setImageLayout] = useState<ImagePlacement[] | null>(null);
  const [seed, setSeed] = useState<number>(() => newSeed());
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [libraryOk, setLibraryOk] = useState(true);
  const [hydrated, setHydrated] = useState(false);

  // The draft slot this page owns, fixed at hydration from the URL target ('new' or
  // 'edit:<id>'). Autosave and post-save clearing only ever touch this key.
  const draftKeyRef = useRef<string>(draftKey(null));
  const [restoreSection, setRestoreSection] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await libraryDb.isAvailable();
      if (cancelled) return;
      setLibraryOk(ok);
      if (!ok) { setHydrated(true); return; }
      await libraryDb.migrateFromLocalStorage().catch((e) => console.warn('[mania] migration failed', e));
      const editId = new URLSearchParams(window.location.search).get('edit') || null;
      const key = draftKey(editId);
      draftKeyRef.current = key;
      const draft = await libraryDb.getDraft(key).catch(() => undefined);
      if (cancelled) return;
      if (draft) {
        await restoreDraft(draft, key);
      } else if (editId) {
        const e = await libraryDb.get(editId).catch(() => undefined);
        if (cancelled) return;
        if (e) {
          setEditingId(e.id); setSeed(e.seed); setCreatedAt(e.createdAt); setDate(new Date(e.date));
          setLocation(e.location); setJournalText(e.text.join('\n\n')); setTextColors(e.textColors);
          setImageLayout(e.imageLayout); setImages(await Promise.all(e.images.map(blobToDataUrl)));
        } else {
          toast.error('That entry no longer exists.');
        }
      }
      if (!cancelled) setHydrated(true);
    })();

    async function restoreDraft(draft: Draft, key: string) {
      setEditingId(draft.editingId); setDate(new Date(draft.date)); setLocation(draft.location);
      setJournalText(draft.journalText); setImages(draft.images); setImageLayout(draft.imageLayout);
      setTextColors(draft.textColors);
      const sec = Number.isFinite(draft.section) ? Math.max(0, Math.min(SECTIONS - 1, Math.round(draft.section))) : 0;
      setRestoreSection(sec);
      if (!draft.editingId) return;
      const e = await libraryDb.get(draft.editingId).catch(() => undefined);
      if (cancelled) return;
      if (e) { setSeed(e.seed); setCreatedAt(e.createdAt); return; }
      // The edited entry is gone: continue as a new entry. Move the words into the 'new'
      // slot only if it's free; otherwise they stay under their own key (never clobber).
      setEditingId(null);
      const newKey = draftKey(null);
      const existingNew = await libraryDb.getDraft(newKey).catch(() => undefined);
      if (cancelled) return;
      if (!existingNew) {
        try {
          await libraryDb.putDraft(newKey, { ...draft, editingId: null, updatedAt: new Date().toISOString() });
          await libraryDb.clearDraft(key);
          draftKeyRef.current = newKey;
        } catch (err) { console.warn('[mania] orphan draft move failed', err); }
        toast.error('That entry no longer exists — your words are kept as a new page.');
      } else {
        toast.error('That entry no longer exists — your draft is kept here.');
      }
    }
    return () => { cancelled = true; };
  }, []);

  // After hydration, put the reader back on the section they left (instant, no smooth scroll).
  useEffect(() => {
    if (!hydrated || restoreSection <= 0) return;
    const raf = requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      const top = (restoreSection / (SECTIONS - 1)) * Math.max(0, el.scrollHeight - el.clientHeight);
      if (lenisRef.current) lenisRef.current.scrollTo(top, { immediate: true, force: true });
      else el.scrollTop = top;
    });
    return () => cancelAnimationFrame(raf);
  }, [hydrated, restoreSection]);

  // Autosave (debounced). The timer lives in a ref so Save can cancel it; the callback
  // bails while a save is in flight so a late tick can't resurrect a cleared draft.
  // The first run after hydration is skipped: just opening a page writes nothing.
  const savingRef = useRef(false);
  const autosaveTimer = useRef<number | null>(null);
  const pendingDraft = useRef<Draft | null>(null);
  const autosaveArmed = useRef(false);
  const writeDraft = useCallback((d: Draft) => {
    libraryDb.putDraft(draftKeyRef.current, d).catch((e) => console.warn('[mania] draft save failed', e));
  }, []);
  useEffect(() => {
    if (!hydrated || !libraryOk) return;
    if (!autosaveArmed.current) { autosaveArmed.current = true; return; }
    pendingDraft.current = { editingId, date: date.toISOString(), location, journalText, images, imageLayout, textColors, section, updatedAt: new Date().toISOString() };
    if (autosaveTimer.current !== null) window.clearTimeout(autosaveTimer.current);
    autosaveTimer.current = window.setTimeout(() => {
      autosaveTimer.current = null;
      const d = pendingDraft.current;
      if (savingRef.current || !d) return;
      pendingDraft.current = null;
      writeDraft(d);
    }, AUTOSAVE_MS);
  }, [hydrated, libraryOk, editingId, date, location, journalText, images, imageLayout, textColors, section, writeDraft]);
  // Leaving mid-debounce must not drop the last keystrokes: flush the pending draft (unless a save owns it).
  // A reload never unmounts, so pagehide covers that path.
  useEffect(() => {
    const flush = () => {
      if (autosaveTimer.current !== null) { window.clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
      const d = pendingDraft.current;
      pendingDraft.current = null;
      if (d && !savingRef.current) writeDraft(d);
    };
    window.addEventListener('pagehide', flush);
    return () => { window.removeEventListener('pagehide', flush); flush(); };
  }, [writeDraft]);

  const wordCount = journalText.trim() ? journalText.trim().split(/\s+/).length : 0;
  useEffect(() => { setWords(wordCount); }, [wordCount, setWords]);
  useEffect(() => () => setWords(0), [setWords]);

  const textSections = useMemo(() => journalText.split('\n\n').filter((s) => s.trim().length > 0), [journalText]);
  const canSave = location.trim().length > 0 && journalText.trim().length > 0;

  // Layout commits are debounced so a drag doesn't re-render the page every frame.
  // The latest uncommitted layout is kept in a ref so Save can flush it synchronously.
  const layoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingLayout = useRef<ImagePlacement[] | null>(null);
  const [layoutEpoch, setLayoutEpoch] = useState(0);
  const setLayoutIfChanged = useCallback((l: ImagePlacement[]) => {
    if (layoutTimer.current) clearTimeout(layoutTimer.current);
    pendingLayout.current = l;
    layoutTimer.current = setTimeout(() => {
      layoutTimer.current = null;
      pendingLayout.current = null;
      setImageLayout((prev) => (prev && JSON.stringify(prev) === JSON.stringify(l) ? prev : l));
    }, 150);
  }, []);
  // Add/remove: full fresh auto-layout (discard manual positions, drop any pending commit,
  // remount the reveal canvas via its key).
  const resetLayout = useCallback(() => {
    if (layoutTimer.current) { clearTimeout(layoutTimer.current); layoutTimer.current = null; }
    pendingLayout.current = null;
    setImageLayout(null);
    setLayoutEpoch((n) => n + 1);
  }, []);
  useEffect(() => () => { if (layoutTimer.current) clearTimeout(layoutTimer.current); }, []);

  const onFiles = async (files: FileList | null) => {
    if (!files) return;
    // An unreadable file resolves to null and is skipped, so one bad file can't hang the batch.
    const reads = Array.from(files).slice(0, 6).map((f) => new Promise<string | null>((res) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result));
      r.onerror = () => res(null);
      r.onabort = () => res(null);
      try { r.readAsDataURL(f); } catch { res(null); }
    }));
    const read = await Promise.all(reads);
    const ok = read.filter((u): u is string => u !== null);
    if (ok.length < read.length) toast.error(read.length - ok.length === 1 ? 'One photo couldn’t be read.' : 'Some photos couldn’t be read.');
    if (!ok.length) return;
    const urls = await Promise.all(ok.map((u) => downscale(u)));
    setImages((prev) => [...prev, ...urls].slice(0, 6));
    resetLayout();
  };

  const onSaved = (id: string) => { setSealing(id); };
  const handleSave = async () => {
    if (!canSave || savingRef.current || !libraryOk) return;
    // Snapshot the page synchronously, before any state change or await can trigger a canvas redraw.
    const canvas = previewRef.current?.querySelector('canvas') as HTMLCanvasElement | null;
    const first = canvas ? snapshotPreview(canvas) : null;
    // Defensive: a sweep once stored a blank preview (not reproducible). If the snapshot has no
    // ink in its top quarter, wait two frames and re-capture once from the live canvas.
    const previewPromise = (async (): Promise<Blob | null> => {
      let shot = first;
      if (canvas && shot && isMostlyBlank(shot)) {
        await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
        shot = snapshotPreview(canvas) ?? shot;
      }
      return shot ? encodePreview(shot) : null;
    })();
    savingRef.current = true; setSaving(true);
    // Cancel any pending autosave so it can't re-create the draft after we clear it.
    if (autosaveTimer.current !== null) { window.clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    // Flush a pending drag: the latest position is what gets saved.
    let finalLayout = imageLayout;
    if (layoutTimer.current) {
      clearTimeout(layoutTimer.current); layoutTimer.current = null;
      if (pendingLayout.current) { finalLayout = pendingLayout.current; setImageLayout(finalLayout); }
      pendingLayout.current = null;
    }
    let saved = false;
    try {
      const clash = await libraryDb.findByDay(date.toISOString(), editingId ?? undefined);
      if (clash) { toast.error('You already have an entry for this day.'); return; }
      const preview = await previewPromise;
      const blobs = (await Promise.all(images.map((u) => downscale(u)))).map(dataUrlToBlob).filter((b): b is Blob => b !== null);
      try { localStorage.setItem(LAST_LOC_KEY, location); } catch {}
      const now = new Date().toISOString();
      const id = editingId ?? newEntryId();
      await libraryDb.put({ id, date: date.toISOString(), location, text: textSections, images: blobs,
        imageLayout: finalLayout, textColors, seed, preview, createdAt: createdAt ?? now, updatedAt: now });
      pendingDraft.current = null;
      await libraryDb.clearDraft(draftKeyRef.current).catch(() => {});
      saved = true;
      onSaved(id);
    } catch (e) {
      console.error('[mania] save failed', e);
      toast.error('Couldn’t save. Your draft is safe — try again.');
    } finally {
      // After success keep the guard and disabled button up while the tear animation runs.
      if (!saved) {
        savingRef.current = false; setSaving(false);
        // Not saved: write any autosave that was cancelled/skipped during the attempt.
        if (pendingDraft.current) { const d = pendingDraft.current; pendingDraft.current = null; writeDraft(d); }
      }
    }
  };

  const introMotion = (reduce || introSkipped) ? { initial: false as const } : introVariant === 'shatter'
    ? { initial: { opacity: 0, scale: 1.6, filter: 'blur(24px)' }, animate: { opacity: 1, scale: 1, filter: 'blur(0px)' }, transition: { duration: 1.1, ease: EXPO } }
    : introVariant === 'scan'
    ? { initial: { clipPath: 'inset(0 0 100% 0)' }, animate: { clipPath: 'inset(0 0 0% 0)' }, transition: { duration: 0.9, ease: 'linear' as const } }
    : { initial: { x: -80, skewX: 25, opacity: 0 }, animate: { x: [-80, 30, -10, 0], skewX: [25, -12, 4, 0], opacity: 1 }, transition: { duration: 0.8, times: [0, 0.4, 0.7, 1] } };

  const sub = (t: string) => <p style={{ color: C.muted, marginTop: 18, fontSize: 17, fontFamily: UI }}>{t}</p>;

  return (
    <div className="w-full relative" style={{ color: C.ink }}>
      {/* background is the distorted-static storm (global <Storm/>), intensifying with scroll */}

      {!libraryOk && (
        <div role="alert" style={{ position: 'fixed', top: 53, left: 0, right: 0, zIndex: 45, textAlign: 'center', padding: '8px 16px',
          background: 'rgba(229,164,77,0.95)', color: '#0b0b0c', fontSize: 13, fontFamily: UI }}>
          Saving isn’t available in this browser (private mode?). Your words won’t be kept.
        </div>
      )}

      {/* scroll progress bar */}
      <div className="storm-chrome" style={{ position: 'fixed', top: 49, left: 0, right: 0, height: 2, background: 'rgba(255,255,255,0.08)', zIndex: 40 }}>
        <motion.div style={{ height: '100%', background: C.ink, width: barWidth, transformOrigin: 'left' }} />
      </div>

      {/* scroll cue — fixed at the bottom, fades the moment you start scrolling */}
      <motion.div style={{ opacity: cueOpacity, position: 'fixed', bottom: 34, left: '50%', x: '-50%', zIndex: 40,
        color: C.muted, fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.25em', pointerEvents: 'none', textAlign: 'center' }}>
        <motion.div animate={reduce ? undefined : { y: [0, 9, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}>
          SCROLL<br />↓
        </motion.div>
      </motion.div>

      <div ref={scrollRef} className="gj-scroll" style={{ position: 'relative', zIndex: 1 }}>
       <div ref={contentRef}>
        {/* INTRO — the animated MANIA mark floating over the distorted static */}
        <Section progress={scrollYProgress} index={0} total={SECTIONS} reduce={reduce}>
          <div className="flex flex-col items-center text-center">
            <motion.div key={introSkipped ? 'done' : 'live'} {...introMotion}>
              <GlitchText as="h1" text="mania" tapBurst tempoMs={200} style={{ fontSize: 'clamp(64px, 14vw, 190px)', justifyContent: 'center' }} />
            </motion.div>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ root: scrollRef, amount: 0.6 }}
              transition={{ delay: 0.6, duration: 0.9 }}
              style={{ color: C.muted, marginTop: 26, fontSize: 17, fontFamily: UI, letterSpacing: '0.01em' }}>
              Let’s make a journal — no pen, just scroll.
            </motion.p>
          </div>
        </Section>

        {/* 01 — WHEN */}
        <Section progress={scrollYProgress} index={1} total={SECTIONS} reduce={reduce}>
          <GlitchText as="h1" text="When did this happen?" style={{ fontSize: 'clamp(40px,6vw,78px)', lineHeight: 1.06 }} />
          {sub('Pick the day you’re writing about.')}
          <div className="guided-date mt-9">
            <DatePicker selected={date}
              onChange={(d: Date | null) => { if (d) { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12); setDate(x); } }}
              dateFormat="EEEE, MMMM d, yyyy" inline />
          </div>
        </Section>

        {/* 02 — WRITE */}
        <Section progress={scrollYProgress} index={2} total={SECTIONS} reduce={reduce}>
          <GlitchText as="h1" text="What happened?" style={{ fontSize: 'clamp(40px,6vw,78px)', lineHeight: 1.06 }} />
          {sub('Just talk. One thought after another.')}
          <textarea value={journalText} onChange={(e) => setJournalText(e.target.value)} onKeyDown={onWriteKey} placeholder="Today I…"
            className="mt-9"
            style={{ width: '100%', minHeight: '42vh', resize: 'none', background: C.field, color: C.ink,
              border: `1px solid ${C.line2}`, borderRadius: 16, padding: '24px 26px', fontSize: 22, lineHeight: 1.6, outline: 'none', fontFamily: UI }} />
          <FollowUp text={journalText} location={location} onUse={(q) => setJournalText((t) => `${t.trimEnd()}\n\n${q}\n`)} />
        </Section>

        {/* 03 — PHOTOS */}
        <Section progress={scrollYProgress} index={3} total={SECTIONS} reduce={reduce}>
          <GlitchText as="h1" text="Add a few photos." style={{ fontSize: 'clamp(40px,6vw,78px)', lineHeight: 1.06 }} />
          {sub('Optional — just scroll past if there are none.')}
          <div className="mt-9">
            <button onClick={() => fileRef.current?.click()} className="gj-dropzone w-full flex flex-col items-center justify-center gap-2"
              style={{ border: `1px dashed ${C.line2}`, borderRadius: 18, padding: '48px 16px', color: C.muted, background: C.field, cursor: 'pointer', fontFamily: UI }}>
              <span style={{ fontSize: 30 }}>↥</span>
              <span style={{ fontSize: 14, letterSpacing: '0.04em' }}>Tap to add photos</span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} />
            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-4">
                {images.map((src, i) => (
                  <PhotoTile key={src} src={src} i={i} reduce={reduce}
                    onRemove={() => { setImages((p) => p.filter((_, j) => j !== i)); resetLayout(); }} />
                ))}
              </div>
            )}
          </div>
        </Section>

        {/* 04 — WHERE */}
        <Section progress={scrollYProgress} index={4} total={SECTIONS} reduce={reduce}>
          <GlitchText as="h1" text="Where were you?" style={{ fontSize: 'clamp(40px,6vw,78px)', lineHeight: 1.06 }} />
          {sub('And pick the ink for your title.')}
          <div className="flex flex-col gap-7 mt-9">
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={DEFAULT_LOC}
              style={{ width: '100%', background: C.field, color: C.ink, border: `1px solid ${C.line2}`, borderRadius: 12, padding: '16px 18px', fontSize: 19, outline: 'none', fontFamily: UI }} />
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.16em', color: C.muted, textTransform: 'uppercase', marginBottom: 12 }}>Title ink</div>
              <SimpleColorPicker colors={textColors} onChange={setTextColors} images={images} />
            </div>
          </div>
        </Section>

        {/* REVEAL */}
        <RevealSection progress={scrollYProgress} index={5} total={SECTIONS} scrollRef={scrollRef} reduce={reduce} previewRef={previewRef}
          date={date} location={location} textSections={textSections} images={images} textColors={textColors}
          imageLayout={imageLayout} onLayout={setLayoutIfChanged} layoutEpoch={layoutEpoch}
          canSave={canSave && libraryOk} saving={saving} sealing={!!sealing} onSave={handleSave} />
        <SaveSequence active={!!sealing} intensity={intensity}
          onDone={() => { if (sealing) tear({ to: `/journal?new=${encodeURIComponent(sealing)}`, strength: 1 }); }} />
       </div>
      </div>
    </div>
  );
};

export default GuidedJournal;
